<template>
  <div class="accessories-form default-form">
    <ValidationObserver ref="form">
      <div class="columns">
        <div class="column">
          <div class="row">
            <validation-provider name="Название" rules="required">
              <template #default="{ errors }">
                <label>Название</label>
                <input
                  type="text"
                  :disabled="readOnly"
                  :class="{ 'form-error': errors[0]}"
                  v-model="model.title"
                  placeholder="Название"
                >
                <div v-if="errors[0]" class="error-descr">{{ errors[0] }}</div>
              </template>
            </validation-provider>
          </div>

          <div class="row">
            <validation-provider name="Статус" rules="required">
              <template #default="{ errors }">
                <label>Статус</label>
                <multiselect
                  v-model="model.status"
                  :options="statuses"
                  :disabled="readOnly"
                  :class="{ 'form-error': errors[0]}"
                  track-by="id"
                  label="title"
                  placeholder="Статус"
                />
                <div v-if="errors[0]" class="error-descr">{{ errors[0] }}</div>
              </template>
            </validation-provider>
          </div>

          <div class="row">
            <validation-provider name="Описание" rules="required">
              <template #default="{ errors }">
                <label>Описание</label>
                <textarea
                  v-model="model.description"
                  :disabled="readOnly"
                  :class="{ 'form-error': errors[0]}"
                  rows="10"
                  placeholder="Описание"
                />
                <div v-if="errors[0]" class="error-descr">{{ errors[0] }}</div>
              </template>
            </validation-provider>
          </div>

          <div class="row">
            <validation-provider name="Артикул" rules="required">
              <template #default="{ errors }">
                <label>Артикул</label>
                <input
                  type="text"
                  :disabled="readOnly"
                  :class="{ 'form-error': errors[0]}"
                  v-model="model.vendor_code"
                  placeholder="Артикул"
                >
                <div v-if="errors[0]" class="error-descr">{{ errors[0] }}</div>
              </template>
            </validation-provider>
          </div>

          <div class="row">
            <label>Комментарий</label>
            <textarea
              v-model="model.comment"
              :disabled="readOnly"
              rows="10"
              placeholder="Комментарий"
            />
          </div>
        </div>

        <div class="column img">
          <div class="row">
            <validation-provider name="Изображения" rules="required">
              <template #default="{ errors }">
                <label>Изображения (макс. {{ maxFiles }})</label>
                <vue-dropzone
                  id="dropzone"
                  ref="imageDropzone"
                  v-model="model.image_urls"
                  :options="dropzoneOptions"
                  :destroyDropzone="false"
                  :class="{ 'form-error': errors[0], 'max-exceeded': maxFilesExceeded}"
                  @vdropzone-success="success"
                  @vdropzone-mounted="dropzoneMounted"
                  @vdropzone-removed-file="dropzoneFileRemoved"
                  @vdropzone-file-added="dropzoneAdded"
                />
                <div v-if="errors[0]" class="error-descr">{{ errors[0] }}</div>
              </template>
            </validation-provider>
          </div>

          <div class="row">
            <validation-provider name="Раздел" rules="required">
              <template #default="{ errors }">
                <label>Раздел</label>
                <treeselect
                  v-model="model.category_id"
                  :options="treeCategories"
                  :disabled="readOnly"
                  :class="{ 'form-error': errors[0]}"
                  :searchable="true"
                  :clearable="true"
                  :disable-branch-nodes="true"
                  :normalizer="normalize"
                  value-consists-of="LEAF_PRIORITY"
                />
                <!-- TODO: Задать __control { border-color: var(--main-border-color); } в файле assets/styles/treeselect.scss -->
                <div v-if="errors[0]" class="error-descr">{{ errors[0] }}</div>
              </template>
            </validation-provider>
          </div>

          <div class="row">
            <validation-provider name="Цена" rules="required">
              <template #default="{ errors }">
                <label>Цена, ₽</label>
                <input
                  type="number"
                  :disabled="readOnly"
                  :class="{ 'form-error': errors[0]}"
                  v-model.number="model.price"
                  placeholder="Цена"
                >
                <div v-if="errors[0]" class="error-descr">{{ errors[0] }}</div>
              </template>
            </validation-provider>
          </div>

          <div class="row">
            <validation-provider name="В наличии" rules="required">
              <template #default="{ errors }">
                <label>В наличии</label>
                <multiselect
                  v-model="model.availability_status"
                  :options="availabilityStatuses"
                  :disabled="readOnly"
                  :class="{ 'form-error': errors[0]}"
                  track-by="id"
                  label="title"
                  placeholder="Выберите вариант"
                />
                <div v-if="errors[0]" class="error-descr">{{ errors[0] }}</div>
              </template>
            </validation-provider>
          </div>
        </div>
      </div>
    </ValidationObserver>
  </div>
</template>

<script>
import vue2Dropzone from 'vue2-dropzone';

import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';

import { mapActions, mapGetters } from 'vuex';

import options from '@/config/dropzone-options';

import { ValidationProvider, ValidationObserver } from 'vee-validate';

export default {
  name: 'accessories-form',

  components: {
    vueDropzone: vue2Dropzone,
    Treeselect,
    ValidationProvider,
    ValidationObserver,
  },

  props: {
    model: Object,
  },

  data() {
    return {
      maxFiles: 10,
    };
  },

  computed: {
    ...mapGetters({
      statuses: 'accessories/statuses',
      treeCategories: 'accessoryCategories/treeCategories',
      availabilityStatuses: 'accessories/availabilityStatuses',
      uploadResponse: 'util/uploadResponse',
    }),

    dropzoneOptions() {
      return {
        ...options,
        url: `${process.env.VUE_APP_STATIC_URL}c/audi`,
        addRemoveLinks: true,
        maxFiles: this.maxFiles,
        dictDefaultMessage: 'Нажмите, чтобы добавить еще одно изображение',
      };
    },

    readOnly() {
      return !!this.model.deleted_at;
    },

    maxFilesExceeded() {
      return this.model.image_urls
        ? this.model.image_urls.length > this.maxFiles
        : false;
    },
  },

  methods: {
    ...mapActions({
      uploadStaticFile: 'util/uploadStaticFile',
      removeStaticFile: 'util/removeStaticFile',
    }),

    async dropzoneAdded(file) {
      if (!file.manuallyAdded) {
        const formData = new FormData();
        formData.append('file', file);

        await this.uploadStaticFile({
          formData,
          listener: this.updateProgress,
          file,
        });

        file.url = this.getUrlByFileName(this.uploadResponse.data.data.name);

        this.$refs.imageDropzone.dropzone
          .emit('complete', file)
          .emit('success', file, this.uploadResponse);
      }
    },

    updateProgress(event, file) {
      const percentCompleted = Math.floor((event.loaded * 100) / event.total);

      this.$refs.imageDropzone.dropzone.emit(
        'uploadprogress',
        file,
        percentCompleted
      );
    },

    success(file, response) {
      const url = process.env.VUE_APP_CDN_STATIC_URL + response.data.data.name;
      this.$emit('imageUploaded', url);
    },

    async validate() {
      return await this.$refs.form.validate();
    },

    async dropzoneFileRemoved(file) {
      const url = this.extractUrl(file);
      if (url) {
        await this.removeStaticFile(url);
        this.$emit('imageDeleted', url);
      } else {
        console.warn('No image to delete');
      }
    },

    extractUrl(file) {
      if (file.url) {
        return file.url;
      } else {
        if (file.xhr) {
          if (file.xhr.response) {
            const response = JSON.parse(file.xhr.response);
            return response.data
              ? this.getUrlByFileName(response.data.name)
              : false;
          } else {
            return false;
          }
        } else {
          return false;
        }
      }
    },

    getUrlByFileName(name) {
      return process.env.VUE_APP_STATIC_URL + name;
    },

    dropzoneMounted() {
      if (this.model.image_urls) {
        this.model.image_urls.forEach(url => {
          this.addFileByUrl(url);
        });
      }
    },

    addFileByUrl(url) {
      this.$refs.imageDropzone.manuallyAddFile(
        {
          url,
          size: null,
          name: url,
        },
        url
      );
    },

    normalize(node) {
      return {
        ...node,
        label: node.title,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.accessories-form ::v-deep .dz-max-files-reached {
  .dz-message {
    display: none !important;
  }
}

.accessories-form ::v-deep .dropzone.dz-started {
  .dz-message {
    display: block;
  }
}
</style>
